import { useCallback, useReducer, useEffect, useRef, useMemo } from "react";

import { Hash as SHA256Hash } from "fast-sha256";

import {
  UploadEntry,
  FileUploadEntry,
  OrchidDriveUploadTask,
  FileEntry,
} from "../types";
import OrchidDriveFileUploadStatusReducer from "../reducers/orchidDriveFileUploadStatus";
import { createFileEntry, uploadChunk, updateFileDigest } from "./fileUpload";
import { sleep } from "utils/fn";
import { createEntry } from "./entry";
import {
  AddPendingDriveEntriesFunc,
  UpdateAvailableDriveEntriesFunc,
} from "./orchidDriveData";
import { AddEntryKeysFunc } from "./orchidDriveEntryKeys";
import { hexEncode } from "utils/OrchidEncryption";
import { useUser } from "contexts/UserContext";
import OrchidDriveUploadTasksReducer from "../reducers/orchidDriveUploadTasks";
import { directoryEntryCreate } from "./directoryEntry";
import {
  abandonUploadTask,
  createUploadTask,
  finalizeUploadTask,
} from "./uploadTask";
import { calculateChunkCount } from "../../../utils/calculateChunkCount";
import getUniqueOrchidDeviceId from "utils/orchidDeviceId";

// in bytes
export const DEFAULT_CHUNK_SIZE = 500000;

const orchidUniqueDeviceId = getUniqueOrchidDeviceId();

export type DriveUploadFunc = (
  storageDrive: StorageDrive,
  patientUserId: string | null,
  files: FileUploadEntry[],
) => Promise<OrchidDriveUploadTask | undefined>;

export type DriveUploadAbortFunc = (entry: UploadEntry) => Promise<void>;

export type UploadFileContinueFunc = (fileDataId: string, file: File) => void;

export type LocalUploadTaskStatus =
  | {
      uploadEntry: UploadEntry;
      uploadTaskId: null | string;
      status: "waiting";
    }
  | {
      uploadEntry: UploadEntry;
      status: "creating upload task";
    }
  | {
      uploadEntry: UploadEntry;
      uploadTaskId: string;
      status: "creating directory";
      parentDirectoryId: null | string;
      name: string;
    }
  | {
      uploadEntry: UploadEntry;
      uploadTaskId: string;
      status: "uploading file";
      fileEntry: FileEntry;
      parentDirectoryId: null | string;
    };

function calculateTimeToSleep(retryCount: number) {
  return retryCount === 0
    ? 0
    : Math.pow(2, Math.min(retryCount - 1, 6)) *
        1000 *
        (1.0 + Math.random() * 0.5);
}

function calculateTotalExpectedSize(files: FileUploadEntry[]): number {
  return files.reduce(
    (accu, fileEntry) =>
      accu +
      calculateChunkCount(fileEntry.file?.file?.size, DEFAULT_CHUNK_SIZE) *
        DEFAULT_CHUNK_SIZE,
    0,
  );
}

function dispatchUploadTaskUpdate(
  dispatcher,
  uploadTasks: OrchidDriveUploadTask[],
) {
  dispatcher({
    type: "update",
    uploadTasks: uploadTasks,
    oldTimeStampThreshold: new Date().getTime() - 60 * 60 * 1000,
  });
}

export function FileUploadProgress(
  updateAvailableDriveEntries: UpdateAvailableDriveEntriesFunc,
  addPendingDriveEntries: AddPendingDriveEntriesFunc,
  addEntryKeys: AddEntryKeysFunc,
) {
  const { user } = useUser();
  const userSub = user?.sub;

  const digests = useRef(new Map());

  const [
    {
      currentCreateUploadTask,
      currentCreateDirectory,
      currentFileEntryToCreate,
      currentUploadChunk,
      driveUploadEntries,
      finishedUploadTaskIds,
    },
    orchidDriveFileUploadStatusDispatch,
  ] = useReducer(OrchidDriveFileUploadStatusReducer, {
    currentCreateUploadTask: null,
    currentFileEntryToCreate: null,
    currentCreateDirectory: null,
    currentUploadChunk: null,
    driveUploadEntries: [],
    finishedUploadTaskIds: {
      uploadTaskIds: [],
      retryCount: 0,
    },
  });

  const [uploadTasks, orchidDriveUploadTasksDispatch] = useReducer(
    OrchidDriveUploadTasksReducer,
    new Map(),
  );

  useEffect(() => {
    orchidDriveFileUploadStatusDispatch({ type: "reset" });
    orchidDriveUploadTasksDispatch({ type: "reset" });
  }, [userSub]);

  useEffect(() => {
    const filesInProgress: Set<string> = new Set();
    for (const entry of driveUploadEntries) {
      for (const f of entry.fileUploadProgress.values()) {
        const fileDataId = f.fileEntryInfo?.fileDataId;
        if (fileDataId) filesInProgress.add(fileDataId);
      }
    }
    for (const [fileDataId] of digests.current.keys()) {
      if (!filesInProgress.has(fileDataId)) {
        digests.current.delete(fileDataId);
      }
    }
  }, [driveUploadEntries]);

  // move to L470
  // useEffect(() => {
  //     if (!currentCreateUploadTask) return;
  //     let abortController: null | AbortController = new AbortController();

  //     (async function () {
  //       if (abortController.signal.aborted) return;

  //       const timeToSleep = calculateTimeToSleep(currentCreateUploadTask.retryCount);
  //       if (timeToSleep > 0) {
  //         await sleep(timeToSleep);
  //         if (abortController.signal.aborted) return;
  //       }

  //       try {

  //         const uploadTaskEntry = await createUploadTask(
  //           currentCreateUploadTask.totalExpectedSize,
  //           orchidUniqueDeviceId,
  //           abortController,
  //         );
  //         if (abortController.signal.aborted) return;

  //         dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [uploadTaskEntry]);
  //         orchidDriveFileUploadStatusDispatch({
  //             type: "upload_task_created",
  //             uploadEntry: currentCreateUploadTask.uploadEntry,
  //             uploadTaskId: uploadTaskEntry.uploadTaskId,
  //           });
  //       } catch (err: any) {
  //         if (err.name === "AbortError") {
  //           // this request was cancelled
  //           return
  //         }
  //         orchidDriveFileUploadStatusDispatch({
  //             type: "upload_task_create_failed",
  //             uploadEntry: currentCreateUploadTask.uploadEntry,
  //           });
  //       }
  //     })();
  //     return () => {
  //         if (abortController) abortController.abort();
  //       };
  //   }, [currentCreateUploadTask]);

  useEffect(() => {
    if (!currentCreateDirectory) return;
    let abortController: null | AbortController = new AbortController();

    (async function () {
      if (abortController.signal.aborted) return;

      const timeToSleep = calculateTimeToSleep(
        currentCreateDirectory.retryCount,
      );
      if (timeToSleep > 0) {
        await sleep(timeToSleep);
        if (abortController.signal.aborted) return;
      }

      const directoryName: string = currentCreateDirectory.pathToCreate[0];

      try {
        const {
          cipherKeys,
          directoryEntry,
          uploadTaskEntry: uploadTaskEntryUpdate,
        } = await directoryEntryCreate(
          directoryName,
          currentCreateDirectory.baseDirectoryId,
          currentCreateDirectory.uploadEntry.storageDrive,
          currentCreateDirectory.uploadEntry.patientUserId,
          currentCreateDirectory.uploadTaskId,
          orchidUniqueDeviceId,
          abortController,
        );
        if (abortController.signal.aborted) return;

        if (uploadTaskEntryUpdate) {
          dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [
            uploadTaskEntryUpdate,
          ]);
        }

        addEntryKeys({ [cipherKeys.entryId]: cipherKeys });
        updateAvailableDriveEntries([], [directoryEntry]);

        orchidDriveFileUploadStatusDispatch({
          type: "directory_created",
          uploadEntry: currentCreateDirectory.uploadEntry,
          fileEntry: currentCreateDirectory.fileEntry,
          createdDirectoryName: directoryName,
          createdDirectoryId: directoryEntry.directoryId,
        });
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this request was cancelled
          return;
        }
        orchidDriveFileUploadStatusDispatch({
          type: "directory_create_failed",
          uploadEntry: currentCreateDirectory.uploadEntry,
          fileEntry: currentCreateDirectory.fileEntry,
        });
      }
    })();
    return () => {
      if (abortController) abortController.abort();
    };
  }, [currentCreateDirectory, updateAvailableDriveEntries, addEntryKeys]);

  useEffect(() => {
    if (!currentFileEntryToCreate) return;
    let abortController: null | AbortController = new AbortController();

    (async function () {
      if (abortController.signal.aborted) return;

      const timeToSleep = calculateTimeToSleep(
        currentFileEntryToCreate.retryCount,
      );
      if (timeToSleep > 0) {
        await sleep(timeToSleep);
        if (abortController.signal.aborted) return;
      }

      try {
        const cipherKeys = await createEntry(
          currentFileEntryToCreate.uploadEntry.storageDrive,
          "file",
        );
        const expectedChunkCount = calculateChunkCount(
          currentFileEntryToCreate.fileEntry.file.size,
          DEFAULT_CHUNK_SIZE,
        );
        const { fileEntry: newEntry, uploadTaskEntry: uploadTaskEntryUpdate } =
          await createFileEntry(
            cipherKeys.entryId,
            currentFileEntryToCreate.fileEntry.name,
            DEFAULT_CHUNK_SIZE,
            expectedChunkCount,
            currentFileEntryToCreate.parentDirectoryId,
            cipherKeys.metaDataCipherKey,
            currentFileEntryToCreate.uploadEntry.patientUserId,
            currentFileEntryToCreate.uploadTaskId,
            orchidUniqueDeviceId,
            abortController,
          );
        if (abortController.signal.aborted) return;

        addEntryKeys({ [cipherKeys.entryId]: cipherKeys });
        updateAvailableDriveEntries([newEntry], []);
        dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [
          uploadTaskEntryUpdate,
        ]);
        orchidDriveFileUploadStatusDispatch({
          type: "file_entry_created",
          uploadEntry: currentFileEntryToCreate.uploadEntry,
          fileEntry: currentFileEntryToCreate.fileEntry,
          fileDataId: cipherKeys.entryId,
          cipherKeys: cipherKeys,
          chunkSize: newEntry.chunkSize,
        });
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this request was cancelled
          return;
        }
        orchidDriveFileUploadStatusDispatch({
          type: "file_entry_create_failed",
          uploadEntry: currentFileEntryToCreate.uploadEntry,
          fileEntry: currentFileEntryToCreate.fileEntry,
        });
      }
    })();
    return () => {
      if (abortController) abortController.abort();
    };
  }, [currentFileEntryToCreate, updateAvailableDriveEntries, addEntryKeys]);

  useEffect(() => {
    if (!currentUploadChunk) return;
    let abortController: null | AbortController = new AbortController();
    const fileEntryInfo = currentUploadChunk.fileEntryInfo;

    (async function () {
      const currentChunkNum =
        fileEntryInfo.lastUploadedFileChunkNum === null
          ? 0
          : fileEntryInfo.lastUploadedFileChunkNum + 1;
      if (abortController.signal.aborted) return;

      const timeToSleep = calculateTimeToSleep(currentUploadChunk.retryCount);
      if (timeToSleep > 0) {
        await sleep(timeToSleep);
        if (abortController.signal.aborted) return;
      }

      // TODO: handle error reading the chunk
      const chunkEndPos = (currentChunkNum + 1) * fileEntryInfo.chunkSize;
      const lastChunk = chunkEndPos >= currentUploadChunk.fileEntry.file.size;
      const chunk = await currentUploadChunk.fileEntry.file
        .slice(currentChunkNum * fileEntryInfo.chunkSize, chunkEndPos)
        .arrayBuffer();
      if (abortController.signal.aborted) return;

      try {
        let digest = digests.current.get(fileEntryInfo.fileDataId);
        if (digest === undefined) {
          digest = new SHA256Hash();
          digests.current.set(fileEntryInfo.fileDataId, digest);
        }

        digest.update(new Uint8Array(chunk));

        if (lastChunk) {
          // send sha256 checksum
          const checksum = hexEncode(digest.digest());
          await updateFileDigest(fileEntryInfo.fileDataId, checksum);
        }

        const update = await uploadChunk(
          fileEntryInfo.fileDataId,
          currentChunkNum,
          chunk,
          fileEntryInfo.cipherKeys.dataCipherKey,
          fileEntryInfo.cipherKeys.metaDataCipherKey,
          lastChunk,
          abortController,
        );
        if (abortController.signal.aborted) return;

        updateAvailableDriveEntries([update], []);
        orchidDriveFileUploadStatusDispatch({
          type: "upload_chunk_progress",
          fileDataId: fileEntryInfo.fileDataId,
          lastUploadedFileChunkNum: currentChunkNum,
          completed: lastChunk,
        });

        // wedon't need to digest anymore, upload was completed
        if (lastChunk) {
          digests.current.delete(fileEntryInfo.fileDataId);
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this upload was cancelled
          return;
        }
        orchidDriveFileUploadStatusDispatch({
          type: "upload_chunk_failed",
          fileDataId: fileEntryInfo.fileDataId,
        });
      }
    })();
    return () => {
      if (abortController) abortController.abort();
    };
  }, [currentUploadChunk, updateAvailableDriveEntries]);

  useEffect(() => {
    if (finishedUploadTaskIds.uploadTaskIds.length === 0) return;
    let abortController: null | AbortController = new AbortController();

    (async function () {
      if (abortController.signal.aborted) return;

      const timeToSleep = calculateTimeToSleep(
        finishedUploadTaskIds.retryCount,
      );
      if (timeToSleep > 0) {
        await sleep(timeToSleep);
        if (abortController.signal.aborted) return;
      }

      const uploadTaskId: string = finishedUploadTaskIds.uploadTaskIds[0];

      try {
        const { fileEntries, directoryEntries, uploadTaskEntry } =
          await finalizeUploadTask(uploadTaskId, abortController);
        if (abortController.signal.aborted) return;

        dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [
          uploadTaskEntry,
        ]);

        orchidDriveFileUploadStatusDispatch({
          type: "finalize_upload",
          uploadTaskId,
        });

        addPendingDriveEntries(fileEntries, directoryEntries);
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this upload was cancelled
          return;
        }
        orchidDriveFileUploadStatusDispatch({
          type: "finalize_upload_failed",
          uploadTaskId,
        });
      }
    })();

    return () => {
      if (abortController) abortController.abort();
    };
  }, [finishedUploadTaskIds, addPendingDriveEntries]);

  const driveUploadFunc: DriveUploadFunc = useCallback(
    async (
      storageDrive: StorageDrive,
      patientUserId: string | null,
      files: FileUploadEntry[],
    ) => {
      if (files.length === 0) {
        throw new Error("no files to upload");
      }
      const uploadEntry = new UploadEntry(storageDrive, patientUserId, files);
      orchidDriveFileUploadStatusDispatch({
        type: "upload_new",
        uploadEntry: uploadEntry,
        totalExpectedSize: calculateTotalExpectedSize(files),
      });

      const currentCreateUploadTask = {
        uploadEntry: uploadEntry,
        totalExpectedSize: calculateTotalExpectedSize(files),
        retryCount: 0,
      };

      try {
        const uploadTaskEntry = await createUploadTask(
          currentCreateUploadTask.totalExpectedSize,
          orchidUniqueDeviceId,
          // abortController,
        );

        dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [
          uploadTaskEntry,
        ]);
        orchidDriveFileUploadStatusDispatch({
          type: "upload_task_created",
          uploadEntry: currentCreateUploadTask.uploadEntry,
          uploadTaskId: uploadTaskEntry.uploadTaskId,
        });

        return uploadTaskEntry;
      } catch (err: any) {
        if (err.name === "AbortError") {
          // this request was cancelled
          return;
        }
        orchidDriveFileUploadStatusDispatch({
          type: "upload_task_create_failed",
          uploadEntry: currentCreateUploadTask.uploadEntry,
        });
      }
    },
    [],
  );

  const driveUploadAbortFunc: DriveUploadAbortFunc = useCallback(
    async (uploadEntryOrTaskId: UploadEntry | string) => {
      if (typeof uploadEntryOrTaskId === "string") {
        orchidDriveFileUploadStatusDispatch({
          type: "upload_abort_by_task_id",
          uploadTaskId: uploadEntryOrTaskId,
        });
      } else {
        orchidDriveFileUploadStatusDispatch({
          type: "upload_abort",
          uploadEntry: uploadEntryOrTaskId,
        });
      }

      const uploadTaskId =
        typeof uploadEntryOrTaskId === "string"
          ? uploadEntryOrTaskId
          : driveUploadEntries.find(
              (e) => e.uploadEntry === uploadEntryOrTaskId,
            )?.uploadTaskId;

      if (uploadTaskId) {
        const { fileEntries, directoryEntries, uploadTaskEntry } =
          await abandonUploadTask(uploadTaskId);

        orchidDriveFileUploadStatusDispatch({
          type: "finalize_upload",
          uploadTaskId,
        });

        dispatchUploadTaskUpdate(orchidDriveUploadTasksDispatch, [
          uploadTaskEntry,
        ]);

        addPendingDriveEntries(fileEntries, directoryEntries);
      }
    },
    [driveUploadEntries, addPendingDriveEntries],
  );

  /* TODO const uploadFileContinueFunc: UploadFileContinueFunc = useCallback(async (
        fileDataId: string,
        file: File,
      ) =>
    {
      const fileMetaData = orchidDriveFiles.files[fileDataId];
      if (!fileMetaData) throw new FileMetaDataUnavailableError(fileDataId);
      const keys = orchidDriveEntryKeys[fileDataId];
      if (!keys) throw new DriveEntryKeysUnavailableError(fileDataId);
      const fileUploadProgress = fileUploadContinue(
          fileMetaData,
          file,
          keys.dataCipherKey,
          keys.metaDataCipherKey,
        );
      for await (const update of fileUploadProgress) {
        orchidDriveFilesDispatch({
            type: "apply_updates",
            fileUpdates: [update],
            directoryUpdates: [],
          });
      }
    }, [orchidDriveEntryKeys, orchidDriveFiles]);*/

  const localUploadTaskStatus: LocalUploadTaskStatus[] = useMemo(() => {
    return driveUploadEntries.map((entry) => {
      if (entry.uploadEntry === currentCreateUploadTask?.uploadEntry) {
        return {
          uploadEntry: entry.uploadEntry,
          status: "creating upload task",
        };
      }
      if (entry.uploadEntry === currentCreateDirectory?.uploadEntry) {
        return {
          uploadEntry: entry.uploadEntry,
          uploadTaskId: currentCreateDirectory.uploadTaskId,
          status: "creating directory",
          parentDirectoryId: currentCreateDirectory.baseDirectoryId,
          name: currentCreateDirectory.pathToCreate[0],
        };
      }
      if (entry.uploadEntry === currentFileEntryToCreate?.uploadEntry) {
        return {
          uploadEntry: entry.uploadEntry,
          uploadTaskId: currentFileEntryToCreate.uploadTaskId,
          status: "uploading file",
          fileEntry: currentFileEntryToCreate.fileEntry,
          parentDirectoryId: currentFileEntryToCreate.parentDirectoryId,
        };
      }
      if (entry.uploadEntry === currentUploadChunk?.uploadEntry) {
        return {
          uploadEntry: entry.uploadEntry,
          uploadTaskId: currentUploadChunk.uploadTaskId,
          status: "uploading file",
          fileEntry: currentUploadChunk.fileEntry,
          parentDirectoryId: currentUploadChunk.parentDirectoryId,
        };
      }
      return {
        uploadEntry: entry.uploadEntry,
        uploadTaskId: entry.uploadTaskId,
        status: "waiting",
      };
    });
  }, [
    driveUploadEntries,
    currentCreateUploadTask,
    currentCreateDirectory,
    currentFileEntryToCreate,
    currentUploadChunk,
  ]);

  return {
    uploadTaskUpdates: uploadTasks,
    localUploadTaskStatus,

    driveUpload: driveUploadFunc,
    driveUploadAbort: driveUploadAbortFunc,
    // uploadFileContinue: uploadFileContinueFunc,
  };
}
